import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { IdCard, Pencil, Save, Settings2, Plus, Trash2, ShieldAlert, Package } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiErrorMessage } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";

const eur = (n) => (n == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n));

export default function EmpleadosTab({ canViewEvolucion }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const profilesQ = useQuery({ queryKey: ["rrhh-profiles"], queryFn: async () => (await api.get("/rrhh/profiles")).data });
  const policyQ = useQuery({ queryKey: ["rrhh-policy"], queryFn: async () => (await api.get("/rrhh/policy")).data });

  const saveMut = useMutation({
    mutationFn: async ({ user_id, ...rest }) => (await api.put(`/rrhh/profiles/${user_id}`, rest)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-profiles"] }); setEditing(null); toast.success("Ficha guardada"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const policyMut = useMutation({
    mutationFn: async (days_per_year) => (await api.put("/rrhh/policy", { days_per_year })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["rrhh-policy"] }); qc.invalidateQueries({ queryKey: ["rrhh-remote"] }); toast.success("Política actualizada"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const rows = profilesQ.data?.rows || [];

  return (
    <div className="space-y-6" data-testid="rrhh-empleados">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><IdCard className="h-6 w-6 text-primary" />Fichas de empleado</h1>
        <p className="text-sm text-muted-foreground">Datos de RRHH del equipo. La <strong>fecha de incorporación</strong> activa la regla de los 3 primeros meses sin teletrabajo.</p>
      </div>

      <PolicyCard policy={policyQ.data} loading={policyQ.isLoading} onSave={(v) => policyMut.mutate(v)} saving={policyMut.isPending} />

      {profilesQ.isLoading ? <Skeleton className="h-64 w-full" /> : (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="p-3">Persona</th><th className="p-3">Equipo</th><th className="p-3">Incorporación</th>
                <th className="p-3">Guardias</th><th className="p-3">Tel. personal</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y" data-testid="emp-table">
              {rows.map((r) => (
                <tr key={r.user_id} data-testid={`emp-row-${r.user_id}`}>
                  <td className="p-3"><span className="flex items-center gap-2"><UserAvatar name={r.full_name} color={r.color} size="xs" /><span><span className="font-medium">{r.full_name}</span><span className="block text-[11px] text-muted-foreground">{r.email}</span></span></span></td>
                  <td className="p-3">{r.equipo || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3">{r.fecha_incorporacion || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3">{r.hace_guardias
                    ? <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700" data-testid={`emp-guardias-${r.user_id}`}><ShieldAlert className="mr-1 h-3 w-3" />Sí</Badge>
                    : <span className="text-muted-foreground">No</span>}</td>
                  <td className="p-3">{r.telefono_personal || <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 text-right"><Button size="sm" variant="outline" className="h-8" onClick={() => setEditing(r)} data-testid={`emp-edit-${r.user_id}`}><Pencil className="mr-1 h-3.5 w-3.5" />Editar</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent></Card>
      )}

      {editing && <FichaDialog row={editing} canViewEvolucion={canViewEvolucion} onClose={() => setEditing(null)} onSave={(p) => saveMut.mutate(p)} saving={saveMut.isPending} />}
    </div>
  );
}

function PolicyCard({ policy, loading, onSave, saving }) {
  const [days, setDays] = useState("");
  const current = policy?.days_per_year ?? 10;
  const val = days === "" ? current : days;
  return (
    <Card><CardContent className="flex flex-wrap items-end gap-4 p-4">
      <Settings2 className="h-5 w-5 text-primary" />
      <div className="space-y-1.5">
        <Label>Días de teletrabajo al año (cupo por persona)</Label>
        {loading ? <Skeleton className="h-9 w-24" /> : (
          <Input type="number" min={0} max={60} className="h-9 w-28" value={val} onChange={(e) => setDays(e.target.value)} data-testid="policy-days" />
        )}
      </div>
      <Button onClick={() => onSave(Number(val))} disabled={saving || Number(val) === current} data-testid="policy-save"><Save className="mr-1.5 h-4 w-4" />{saving ? "Guardando…" : "Guardar política"}</Button>
      <p className="text-[11px] text-muted-foreground">Según el documento «Días de teletrabajo al año» del Employee Site.</p>
    </CardContent></Card>
  );
}

const TEAMS = ["Ventas", "Reservas", "Traveler Support", "Administración", "Dirección", "Contabilidad", "Facturación"];

const BASIC_FIELDS = [
  ["fecha_incorporacion", "Fecha de incorporación", "date"],
  ["equipo", "Equipo / Departamento", "select"],
  ["telefono_personal", "Teléfono personal", "text"],
  ["email_personal", "Email personal", "email"],
  ["fecha_nacimiento", "Fecha de nacimiento", "date"],
  ["dni", "DNI / NIE", "text"],
];
const ADDRESS_FIELDS = [
  ["via", "Vía / Calle", "wide"],
  ["numero", "Número", ""],
  ["piso", "Piso / Puerta", ""],
  ["cp", "Código Postal", ""],
  ["localidad", "Localidad", ""],
  ["provincia", "Provincia", ""],
];

function FichaDialog({ row, onClose, onSave, saving, canViewEvolucion }) {
  const [form, setForm] = useState(() => {
    const f = { user_id: row.user_id, contacto_emergencia: row.contacto_emergencia || "", notas: row.notas || "", hace_guardias: !!row.hace_guardias };
    [...BASIC_FIELDS, ...ADDRESS_FIELDS].forEach(([k]) => { f[k] = row[k] || ""; });
    return f;
  });
  const [inventario, setInventario] = useState(() =>
    Array.isArray(row.inventario) ? row.inventario.map((i) => ({ material: i.material || "", notas: i.notas || "" })) : []);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const evolQ = useQuery({
    queryKey: ["rrhh-nomina-evol", row.user_id],
    queryFn: async () => (await api.get("/rrhh/nominas/evolucion", { params: { user_id: row.user_id } })).data,
    enabled: !!canViewEvolucion,
  });
  const points = (evolQ.data?.points || []).map((p) => ({ name: p.periodo_label || p.periodo, Bruto: p.bruto, Neto: p.neto }));

  const addItem = () => setInventario((s) => [...s, { material: "", notas: "" }]);
  const setItem = (i, k, v) => setInventario((s) => s.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const delItem = (i) => setInventario((s) => s.filter((_, idx) => idx !== i));

  const handleSave = () => onSave({ ...form, inventario: inventario.filter((i) => i.material.trim()) });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="emp-dialog">
        <DialogHeader>
          <DialogTitle>Ficha de {row.full_name}</DialogTitle>
          <DialogDescription>Datos internos de RRHH. La fecha de incorporación activa la regla de los 3 primeros meses sin teletrabajo.</DialogDescription>
        </DialogHeader>

        <Section title="Datos básicos">
          <div className="grid gap-3 sm:grid-cols-2">
            {BASIC_FIELDS.map(([k, label, type]) => (
              <div key={k} className="space-y-1.5">
                <Label>{label}</Label>
                {type === "select" ? (
                  <Select value={form[k] || ""} onValueChange={(v) => set(k, v)}>
                    <SelectTrigger data-testid={`emp-field-${k}`}><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                    <SelectContent>{TEAMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`emp-field-${k}`} />
                )}
              </div>
            ))}
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Contacto de emergencia</Label>
              <Input value={form.contacto_emergencia} onChange={(e) => set("contacto_emergencia", e.target.value)} data-testid="emp-field-contacto_emergencia" />
            </div>
          </div>
        </Section>

        <Section title="Dirección">
          <div className="grid gap-3 sm:grid-cols-2">
            {ADDRESS_FIELDS.map(([k, label, mod]) => (
              <div key={k} className={`space-y-1.5 ${mod === "wide" ? "sm:col-span-2" : ""}`}>
                <Label>{label}</Label>
                <Input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`emp-field-${k}`} />
              </div>
            ))}
          </div>
        </Section>

        <Section title="Guardias">
          <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
            <Switch checked={form.hace_guardias} onCheckedChange={(v) => set("hace_guardias", v)} data-testid="emp-field-hace_guardias" />
            <Label className="cursor-pointer text-sm">Hace guardias — verá la información de guardias en el Portal del empleado.</Label>
          </div>
        </Section>

        <Section title="Inventario de material de empresa" action={<Button size="sm" variant="outline" className="h-8" onClick={addItem} data-testid="emp-inv-add"><Plus className="mr-1 h-3.5 w-3.5" />Añadir material</Button>}>
          {inventario.length === 0 ? (
            <p className="flex items-center gap-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground"><Package className="h-4 w-4" />Sin material asignado.</p>
          ) : (
            <div className="space-y-2" data-testid="emp-inv-list">
              {inventario.map((it, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`emp-inv-row-${i}`}>
                  <Input placeholder="Material (ej. Portátil Dell)" value={it.material} onChange={(e) => setItem(i, "material", e.target.value)} className="flex-1" data-testid={`emp-inv-material-${i}`} />
                  <Input placeholder="Notas (nº serie, estado…)" value={it.notas} onChange={(e) => setItem(i, "notas", e.target.value)} className="flex-1" data-testid={`emp-inv-notas-${i}`} />
                  <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0 text-red-600" onClick={() => delItem(i)} data-testid={`emp-inv-del-${i}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Notas">
          <Textarea value={form.notas} onChange={(e) => set("notas", e.target.value)} rows={2} data-testid="emp-field-notas" />
        </Section>

        {canViewEvolucion && (
        <Section title="Evolución salarial">
          {evolQ.isLoading ? <Skeleton className="h-32 w-full" /> : points.length === 0 ? (
            <p className="rounded-lg border border-dashed p-3 text-xs text-muted-foreground" data-testid="emp-evol-empty">Aún no hay nóminas registradas para este empleado.</p>
          ) : (
            <div className="h-40 w-full" data-testid="emp-evol-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} width={42} />
                  <Tooltip formatter={(v) => eur(v)} />
                  <Area type="monotone" dataKey="Bruto" stroke="#6366f1" fill="#6366f1" fillOpacity={0.15} strokeWidth={2} />
                  <Area type="monotone" dataKey="Neto" stroke="#10b981" fill="#10b981" fillOpacity={0.12} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="emp-save"><Save className="mr-1.5 h-4 w-4" />{saving ? "Guardando…" : "Guardar ficha"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, action, children }) {
  return (
    <div className="space-y-2 border-t pt-4 first:border-t-0 first:pt-0">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
